import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useGetGymAiAssistant } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { Bot, Save, User, Sparkles, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";

import { useToast } from "@/hooks/use-toast";
import { ImageUpload } from "@/components/image-upload";

const PERSONALITY_PRESETS = [
  { value: "friendly", label: "Friendly & Casual", description: "Warm, approachable, uses casual language and encouragement", emoji: "😊" },
  { value: "professional", label: "Professional & Formal", description: "Polished, structured, business-like tone", emoji: "💼" },
  { value: "motivational", label: "Motivational Coach", description: "Energetic, pumped up, uses motivational language", emoji: "🔥" },
  { value: "zen", label: "Zen Master", description: "Calm, mindful, focused on balance and harmony", emoji: "🧘" },
];

const GENDER_OPTIONS = [
  { value: "neutral", label: "Neutral" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const AVATAR_PRESETS = [
  { url: "", label: "Default", initials: "AI" },
  { url: "preset:coach-male", label: "Coach (M)", initials: "🏋️" },
  { url: "preset:coach-female", label: "Coach (F)", initials: "🤸" },
  { url: "preset:sensei", label: "Sensei", initials: "🥋" },
  { url: "preset:bot", label: "Bot", initials: "🤖" },
  { url: "preset:zen", label: "Zen", initials: "🧘" },
];

function getPersonalityGreeting(preset: string, name: string): string {
  switch (preset) {
    case "friendly":
      return `Hey there! I'm ${name}, and I'm here to help you find the perfect trainer. Let's get started! 😊`;
    case "professional":
      return `Welcome. I'm ${name}, your scheduling assistant. I'll help you find a suitable trainer based on your preferences.`;
    case "motivational":
      return `LET'S GO! I'm ${name}, and I'm pumped to help you find an amazing trainer! Ready to crush it? 💪`;
    case "zen":
      return `Welcome, friend. I'm ${name}. Take a breath, and let's mindfully find the right training partner for your journey. 🙏`;
    default:
      return `Hi! I'm ${name}. I'll help you find the right trainer.`;
  }
}

function AvatarDisplay({ avatarUrl, name }: { avatarUrl: string | null; name: string }) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  if (avatarUrl && !avatarUrl.startsWith("preset:") && !imgError) {
    return <img src={avatarUrl} alt={name} className="w-full h-full object-cover" onError={() => setImgError(true)} />;
  }
  const preset = AVATAR_PRESETS.find(p => p.url === (avatarUrl || ""));
  return <span className="text-2xl">{preset?.initials || name.charAt(0).toUpperCase()}</span>;
}

export default function AdminAiAssistant() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: config, refetch } = useGetGymAiAssistant(user?.gymId || "", { query: { enabled: !!user?.gymId, retry: false } });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "Assistant",
    gender: "neutral",
    avatarUrl: "" as string | null,
    personalityPreset: "friendly",
    personalityCustom: "" as string | null,
  });

  useEffect(() => {
    if (config) {
      setForm({
        name: config.name || "Assistant",
        gender: config.gender || "neutral",
        avatarUrl: config.avatarUrl || "",
        personalityPreset: config.personalityPreset || "friendly",
        personalityCustom: config.personalityCustom || "",
      });
    }
  }, [config]);

  async function handleSave() {
    if (!user?.gymId) return;
    setSaving(true);
    try {
      await apiFetch(`/gyms/${user.gymId}/ai-assistant`, {
        method: "PUT",
        body: JSON.stringify(form),
      });
      toast({ title: "AI Assistant updated" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-3xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">AI Assistant</h1>
          <p className="text-muted-foreground">Customize the personality and appearance of your gym's scheduling assistant</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-xl font-display text-white flex items-center gap-2">
                  <User className="w-5 h-5" /> Identity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Assistant Name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Coach Bot, Sensei, Maya"
                    className="bg-background border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Gender Presentation</label>
                  <div className="flex gap-2">
                    {GENDER_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setForm({ ...form, gender: opt.value })}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                          form.gender === opt.value
                            ? "bg-primary text-black font-bold border-primary"
                            : "bg-white/5 text-white hover:bg-white/10 border-white/10"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-xl font-display text-white flex items-center gap-2">
                  <Bot className="w-5 h-5" /> Avatar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-white mb-3 block">Choose a preset avatar</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {AVATAR_PRESETS.map(preset => (
                      <button
                        key={preset.url}
                        onClick={() => setForm({ ...form, avatarUrl: preset.url })}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all border ${
                          (form.avatarUrl || "") === preset.url
                            ? "bg-primary/20 border-primary"
                            : "bg-white/5 border-white/10 hover:bg-white/10"
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-lg">
                          {preset.initials}
                        </div>
                        <span className="text-xs text-muted-foreground">{preset.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <label className="text-sm font-medium text-white mb-2 block">Or upload a custom avatar</label>
                  <ImageUpload
                    currentUrl={form.avatarUrl && !form.avatarUrl.startsWith("preset:") ? form.avatarUrl : undefined}
                    onUpload={(url) => setForm({ ...form, avatarUrl: url })}
                    uploadEndpoint="/uploads/ai-avatar"
                    label="Upload Avatar"
                    shape="circle"
                    size="md"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-xl font-display text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" /> Personality
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PERSONALITY_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => setForm({ ...form, personalityPreset: preset.value })}
                      className={`text-left p-4 rounded-xl transition-all border ${
                        form.personalityPreset === preset.value
                          ? "bg-primary/20 border-primary"
                          : "bg-white/5 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{preset.emoji}</span>
                        <span className="text-sm font-bold text-white">{preset.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{preset.description}</p>
                    </button>
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Custom personality description (optional)</label>
                  <textarea
                    value={form.personalityCustom || ""}
                    onChange={(e) => setForm({ ...form, personalityCustom: e.target.value || null })}
                    placeholder="Describe a unique personality, e.g. 'Speaks like a surfer dude who loves martial arts...'"
                    className="w-full h-24 px-3 py-2 rounded-xl bg-background border border-white/10 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/80">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Assistant Settings"}
            </Button>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-8">
              <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">Live Preview</h3>
              <Card className="glass-panel border-white/5 overflow-hidden">
                <div className="bg-gradient-to-b from-primary/10 to-transparent p-6 flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center text-2xl font-bold text-white overflow-hidden ring-2 ring-primary/30">
                    <AvatarDisplay avatarUrl={form.avatarUrl} name={form.name} />
                  </div>
                  <div className="text-center">
                    <h4 className="text-lg font-bold text-white">{form.name || "Assistant"}</h4>
                    <p className="text-xs text-muted-foreground">Your AI Scheduling Assistant</p>
                  </div>
                </div>
                <CardContent className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm shrink-0 overflow-hidden">
                      <AvatarDisplay avatarUrl={form.avatarUrl} name={form.name} />
                    </div>
                    <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-white/90 flex-1">
                      {getPersonalityGreeting(form.personalityPreset, form.name || "Assistant")}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm shrink-0 overflow-hidden">
                      <AvatarDisplay avatarUrl={form.avatarUrl} name={form.name} />
                    </div>
                    <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-white/90 flex-1">
                      {form.personalityPreset === "friendly" && "What kind of training are you looking for? Pick what sounds fun!"}
                      {form.personalityPreset === "professional" && "Please select your preferred discipline from the options below."}
                      {form.personalityPreset === "motivational" && "What discipline gets you FIRED UP? Let's find your perfect match!"}
                      {form.personalityPreset === "zen" && "What practice calls to you? Let your intuition guide you."}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
